import type { ReactNode } from 'react';

import { Button, NotificationInline } from '@coop/components';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import type { TimelineData } from '../../../common/api/loyalty/externalLoyaltyModels';

import VoucherSplash from '../VoucherSplash/VoucherSplash';
import styles from './BonusTimeline.module.scss';
import ProgressBar from './ProgressBar/ProgressBar';

const MonthHeader = ({ children }: React.PropsWithChildren<{}>) => {
    return <h2 className={styles.MonthNameHeader}>{children}</h2>;
};

const PreHeadline = ({ children, rotated }: React.PropsWithChildren<{ rotated?: boolean }>) => {
    return (
        <span className={classNames(styles.PreHeadline, { [styles.Rotated]: rotated })}>
            {children}
        </span>
    );
};

const Headline = ({ children }: React.PropsWithChildren<{}>) => {
    return <h3 className={styles.Title}>{children}</h3>;
};

const Timeline = ({ data }: { data: TimelineData }) => {
    return (
        <>
            {data.levels.length > 0 && (
                <ProgressBar
                    levels={data.levels}
                    bonusProgressInfoDays={data.bonusProgressInfoDays}
                    bonusProgressMonetary={data.bonusProgressMonetary}
                />
            )}
            {data.extraPointEarningsValueKr > 0 && (
                <div className={styles.ExtraBonusWrapper}>
                    <p>Din Extrabonus just nu:</p>
                    <VoucherSplash
                        value={data.extraPointEarningsValueKr}
                        unit="kr"
                        extraBonus
                        size="small"
                    />
                </div>
            )}
        </>
    );
};

type BonusTimelineProps = {
    data?: TimelineData;
    overviewPageUrl?: string;
    isError: boolean;
    headerSlot?: ReactNode;
};

const BonusTimelineRoot = ({ data, overviewPageUrl, isError, headerSlot }: BonusTimelineProps) => {
    const navigate = useNavigate();

    return (
        <div className={styles.ContainerWrapper}>
            {data && (
                <>
                    {headerSlot}
                    <Timeline data={data} />
                </>
            )}
            <NotificationInline
                variant="critical"
                heading="Något gick fel"
                message="Vi kan inte ladda innehållet just nu, men jobbar på att lösa det."
                show={isError}
                role="alert"
                headingElement="h2"
                centerContent
                className={styles.NotificationError}
            />
            {overviewPageUrl ? (
                <Button
                    onClick={() => navigate(overviewPageUrl)}
                    aria-label="Se mer om din bonus"
                    className={styles.Button}
                >
                    Se bonusöversikt
                </Button>
            ) : null}
        </div>
    );
};

const BonusTimeline = {
    Root: BonusTimelineRoot,
    MonthHeader,
    PreHeadline,
    Headline,
};

export default BonusTimeline;
